import type { BaseDocument } from '../mongo/base-document.interface';
import type { GeoCoordinates, PostalAddress } from '../geolocation';
import type { PhoneNumber } from '../phone';

export const LOCATION_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export type LocationStatus = (typeof LOCATION_STATUS)[keyof typeof LOCATION_STATUS];

export const DIVISION_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export type DivisionStatus = (typeof DIVISION_STATUS)[keyof typeof DIVISION_STATUS];

/**
 * A Division is an embedded sub-document of a Location.
 * It carries a stable `_id` so future entities (e.g. Vehicles) can reference it.
 */
export interface Division {
  _id: string;
  name: string;
  status: DivisionStatus;
}

/**
 * Payload for creating a new Division within a Location (via sub-route).
 */
export interface CreateDivision {
  name: string;
  status?: DivisionStatus;
}

/**
 * Payload for updating an existing Division within a Location (via sub-route).
 */
export type UpdateDivision = Partial<CreateDivision>;

/**
 * Full Location entity as stored in MongoDB.
 * A Location belongs to a Territory within a Tenant.
 */
export interface Location extends BaseDocument {
  address?: PostalAddress;
  color: string;
  coordinates?: GeoCoordinates;
  customerSupportPhone?: PhoneNumber;
  description?: string;
  divisions?: Division[];
  geocodingScore?: number;
  geocodingSource?: 'autocomplete' | 'manual';
  name: string;
  phone?: PhoneNumber;
  status: LocationStatus;
  territoryId: string;
}

/**
 * Payload for creating a new Location.
 */
export type CreateLocation = Omit<Location, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/**
 * Payload for updating an existing Location via PATCH.
 */
export interface UpdateLocation {
  address?: PostalAddress;
  color?: string;
  coordinates?: GeoCoordinates;
  customerSupportPhone?: PhoneNumber | null;
  geocodingScore?: number;
  geocodingSource?: 'autocomplete' | 'manual';
  name?: string;
  phone?: PhoneNumber | null;
  status?: LocationStatus;
}
